import React, { useState, useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { Box, CssBaseline, useMediaQuery, useTheme } from "@mui/material";
import MessageSnackbar from "./reusable_components/SnackBar";
import ResetPassword from "./Authentication/ResetPassword";
import Home from "./pages/Home/Home";
import Dashboard from "./pages/Dashboard";
import ProtectedRoute from "./Authentication/ProtectedRoute";
import Login from "./Authentication/Login";
import Logout from "./Authentication/Logout";
import TopAppBar from "./Headers/TopAppBar/TopAppBar";
import NotFound from "./reusable_components/NotFound";
import ReactSpinner from "./reusable_components/ReactSpinner";
import Cameras from "./pages/Cameras";
import OnlyPublicRoute from "./Authentication/OnlyPublicRoute";
import CameraDetailsPage from "./pages/CameraDetailsPage";
import Profile from "./pages/Profile";
import NewPassword from "./pages/password_reset";
import Sidebar from "./components/Sidebar/Sidebar";
import UserManagementLayout from "./pages/UserManagement/UserManagementLayout";
import AddNewUser from "./pages/UserManagement/AddNewUser";
import AllUsers from "./pages/UserManagement/AllUsers";
import Species from "./pages/UserManagement/Species";

// export const backend_url = "http://127.0.0.1:8000";
export const backend_url = "https://api.tpilums.org.pk";
export const googleMapsApiKey =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  "AIzaSyCzaEvpRf9sblvVA18Sxe829LqnTlUyO8o";

export default function App() {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [miniSidenav, setMiniSidenav] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  // Check if current route is login or other public routes
  const isPublicRoute = ["/login", "/reset-password", "/resetpassword/"].some(
    (path) => location.pathname.startsWith(path),
  );

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
      setMiniSidenav(false);
    }
  }, [isMobile]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setMiniSidenav(!miniSidenav);
    }
  };

  // Calculate sidebar width
  const sidebarWidth = miniSidenav ? 70 : 250;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <CssBaseline />

      {/* Only show TopAppBar on protected routes */}
      {!isPublicRoute && (
        <TopAppBar
          onMenuClick={handleDrawerToggle}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
        />
      )}

      {/* Only show Sidebar on protected routes */}
      {!isPublicRoute && (
        <Box
          component="nav"
          sx={{
            width: { lg: sidebarWidth },
            flexShrink: { lg: 0 },
            display: { xs: sidebarOpen ? "block" : "none", lg: "block" },
          }}
        >
          <Sidebar
            brandName="Camera Trap Menu"
            miniSidenav={miniSidenav}
            setMiniSidenav={setMiniSidenav}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            sx={{
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: sidebarWidth,
                borderRight: "none",
              },
            }}
          />
        </Box>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: {
            xs: "100%",
            lg: !isPublicRoute ? `calc(100% - ${sidebarWidth}px)` : "100%",
          },
          // ml: {
          //   xs: 0,
          //   lg: !isPublicRoute ? `${sidebarWidth}px` : 0,
          // },
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          minHeight: "100vh",
          backgroundColor: isPublicRoute
            ? "#f8fafc"
            : darkMode
              ? "#121212"
              : "#f5f5f5",
          position: "relative",
        }}
      >
        {!isPublicRoute && <Box sx={{ height: 64 }} />}

        {/* Content Container */}
        <Box sx={{ p: isPublicRoute ? 0 : 3 }}>
          <ReactSpinner />
          <Switch>
            <OnlyPublicRoute exact path="/login" Component={Login} />
            <OnlyPublicRoute
              exact
              path="/resetpassword/:uid/:token"
              Component={NewPassword}
            />
            <ProtectedRoute exact path="/home" Component={Home} />
            <ProtectedRoute exact path="/dashboard" Component={Dashboard} />
            <ProtectedRoute exact path="/cameras" Component={Cameras} />
            <ProtectedRoute exact path="/profile" Component={Profile} />
            <ProtectedRoute
              exact
              path="/user-management/all-users"
              component={() => (
                <UserManagementLayout>
                  <AllUsers />
                </UserManagementLayout>
              )}
            />
            <ProtectedRoute
              exact
              path="/user-management/add-user"
              component={() => (
                <UserManagementLayout>
                  <AddNewUser />
                </UserManagementLayout>
              )}
            />
            <ProtectedRoute
              exact
              path="/user-management/species"
              component={() => (
                <UserManagementLayout>
                  <Species />
                </UserManagementLayout>
              )}
            />
            <ProtectedRoute
              exact
              path="/user-management"
              render={() => <Redirect to="/user-management/all-users" />}
            />
            <ProtectedRoute
              exact
              path="/statistics/:id"
              Component={CameraDetailsPage}
            />
            <Route
              exact
              path="/logout"
              render={(props) => <Logout {...props} />}
            />
            <Route
              path="/reset-password"
              render={(props) => <ResetPassword {...props} />}
            />
            <Route path="/not-found" component={NotFound} />
            <Redirect from="/" exact to="/home" />
            <Redirect to="/not-found" />
          </Switch>
          <MessageSnackbar />
        </Box>
      </Box>
    </Box>
  );
}
